import { useState, useEffect, useCallback } from 'react';
import { ScientificFormula } from '../ScientificFormula';
import { Button } from '../ui/button';
import { getDocumentFormulas, getFormulaCropUrl } from '../../lib/api';
import { Check, X, Eye, AlertTriangle, ShieldCheck, ImageIcon } from 'lucide-react';

const CONFIDENCE_BADGE = {
  high: { label: 'Fiable', color: 'text-emerald-400 bg-emerald-400/10 border-emerald-500/30' },
  medium: { label: 'À vérifier', color: 'text-amber-400 bg-amber-400/10 border-amber-500/30' },
  low: { label: 'Incertain', color: 'text-red-400 bg-red-400/10 border-red-500/30' },
};

function confidenceLevel(c) {
  if (c >= 0.98) return 'high';
  if (c >= 0.90) return 'medium';
  return 'low';
}

function FormulaRow({ formula, token }) {
  const [status, setStatus] = useState(formula.auto_accepted ? 'accepted' : (formula.use_fallback_image ? 'fallback' : 'pending'));
  const level = confidenceLevel(formula.confidence);
  const badge = CONFIDENCE_BADGE[level];
  const cropUrl = formula.crop_id ? getFormulaCropUrl(formula.crop_id) : null;
  const tex = (formula.latex || '').replace(/^\$|\$$/g, '');

  return (
    <div
      className={`rounded-lg border p-3 transition-all ${status === 'accepted' ? 'border-emerald-500/30 bg-emerald-500/5' : status === 'rejected' ? 'border-red-500/30 bg-red-500/5' : 'border-border'}`}
      data-testid={`formula-row-${formula.id}`}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${badge.color}`}>{badge.label}</span>
          <span className="text-xs text-muted-foreground">{(formula.confidence * 100).toFixed(0)}%</span>
          <span className="text-xs text-muted-foreground">• {formula.type}</span>
          {formula.source && <span className="text-xs text-muted-foreground">• page {formula.source.source_page}</span>}
        </div>
        {level !== 'high' && status === 'pending' && (
          <div className="flex items-center gap-1">
            <Button
              size="sm"
              variant="ghost"
              className="h-7 px-2 text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10"
              onClick={() => setStatus('accepted')}
              data-testid={`validate-formula-${formula.id}`}
            >
              <Check className="w-3.5 h-3.5 mr-1" /> Valider
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="h-7 px-2 text-red-400 hover:text-red-300 hover:bg-red-500/10"
              onClick={() => setStatus('rejected')}
              data-testid={`reject-formula-${formula.id}`}
            >
              <X className="w-3.5 h-3.5 mr-1" /> Rejeter
            </Button>
          </div>
        )}
        {status === 'accepted' && <span className="text-xs text-emerald-400 flex items-center gap-1"><ShieldCheck className="w-3.5 h-3.5" /> Validée</span>}
        {status === 'rejected' && <span className="text-xs text-red-400 flex items-center gap-1"><X className="w-3.5 h-3.5" /> Rejetée — image source conservée</span>}
      </div>

      <div className="grid grid-cols-2 gap-3">
        {/* Left: Original crop */}
        <div className="rounded-md border border-border bg-white/5 p-2 min-h-[40px] flex flex-col">
          <span className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1 flex items-center gap-1"><ImageIcon className="w-3 h-3" /> Original (source)</span>
          {cropUrl ? (
            <img
              src={cropUrl}
              alt="Crop original"
              className="max-w-full max-h-20 object-contain bg-white rounded"
              crossOrigin="use-credentials"
              data-testid={`crop-img-${formula.id}`}
            />
          ) : (
            <span className="text-xs text-muted-foreground italic">Pas de crop disponible</span>
          )}
          <span className="text-[10px] text-muted-foreground mt-1 truncate">{formula.original}</span>
        </div>

        {/* Right: MathJax reconstruction */}
        <div className="rounded-md border border-border bg-white/5 p-2 min-h-[40px] flex flex-col">
          <span className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1 flex items-center gap-1"><Eye className="w-3 h-3" /> Reconstruction (MathJax)</span>
          {tex ? (
            <div className="bg-white rounded p-1 inline-block" data-testid={`mathjax-render-${formula.id}`}>
              <ScientificFormula id={formula.id} tex={tex} display="inline" />
            </div>
          ) : (
            <span className="text-xs text-muted-foreground italic">Aucune reconstruction</span>
          )}
          <span className="text-[10px] text-muted-foreground mt-1 truncate font-mono">{formula.latex}</span>
        </div>
      </div>
    </div>
  );
}

export function FormulaVerification({ docId, docName }) {
  const [formulas, setFormulas] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await getDocumentFormulas(docId);
      setFormulas(res.data.formulas || []);
      setSummary(res.data.confidence_summary || {});
    } catch { /* ignore */ }
    setLoading(false);
  }, [docId]);

  useEffect(() => { load(); }, [load]);

  if (loading || !formulas.length) return null;

  const needsVerif = formulas.filter(f => f.needs_verification || f.use_fallback_image);
  if (!needsVerif.length && !expanded) {
    // All auto-accepted — show a small success badge
    return (
      <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-xs text-emerald-400" data-testid="all-formulas-ok">
        <ShieldCheck className="w-3.5 h-3.5" />
        {formulas.length} formule{formulas.length > 1 ? 's' : ''} détectée{formulas.length > 1 ? 's' : ''} — toutes fiables
        <button className="ml-auto text-muted-foreground hover:text-emerald-300 underline" onClick={() => setExpanded(true)}>voir</button>
      </div>
    );
  }

  const displayFormulas = expanded ? formulas : needsVerif;

  return (
    <div className="rounded-lg border border-amber-500/20 bg-amber-500/5 p-3 space-y-3" data-testid="formula-verification-panel">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-400" />
          <span className="text-sm font-medium text-amber-300">
            Vérification des formules — {docName}
          </span>
        </div>
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          {summary?.high > 0 && <span className="text-emerald-400">{summary.high} fiable{summary.high > 1 ? 's' : ''}</span>}
          {summary?.medium > 0 && <span className="text-amber-400">{summary.medium} à vérifier</span>}
          {summary?.low > 0 && <span className="text-red-400">{summary.low} incertain{summary.low > 1 ? 's' : ''}</span>}
          <button
            className="text-muted-foreground hover:text-foreground underline"
            onClick={() => setExpanded(e => !e)}
          >
            {expanded ? 'Masquer fiables' : 'Tout voir'}
          </button>
        </div>
      </div>

      <p className="text-xs text-muted-foreground">
        Comparez la formule originale (source PDF) avec la reconstruction MathJax. Validez ou rejetez chaque formule.
      </p>

      <div className="space-y-2 max-h-[400px] overflow-y-auto pr-1">
        {displayFormulas.map(f => (
          <FormulaRow key={f.id} formula={f} />
        ))}
      </div>
    </div>
  );
}
